import { useState, useEffect, useCallback } from "react"
import {
  supabase,
  getSubscription,
  getAccessRights,
  startTrial,
  type AccessRightsRow,
  type SubscriptionPlan,
  type SubscriptionRow,
  type SubscriptionStatus,
} from "@/lib/supabase"

// Formule payée en libre-service aujourd'hui. Le serveur choisit le prix actif de
// la formule dans `stripe_prices` : aucun price_id ne transite par le navigateur.
const SELF_SERVE_PLAN: SubscriptionPlan = "autonomie"
export const EBOOK_PAYMENT_LINK = "https://buy.stripe.com/14A00j0LS3IAfyH9eoa3u00"

/**
 * Jours restants avant la fin de l'essai, arrondis au jour entamé.
 *
 * L'échéance tombe au matin de J8 à 07:00 Paris : le dernier jour affiche donc
 * « 1 jour » toute la journée de J7, ce qui est ce que vit le client.
 */
function computeTrialDaysLeft(trialEndsAt: string | null): number {
  if (!trialEndsAt) return 0
  const diff = new Date(trialEndsAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null)
  const [access, setAccess] = useState<AccessRightsRow | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchSubscription = useCallback(async () => {
    setLoading(true)
    try {
      const [sub, rights] = await Promise.all([getSubscription(), getAccessRights()])
      setSubscription(sub)
      setAccess(rights)
    } catch (err) {
      console.error("[useSubscription] fetch error:", err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  /**
   * Active la semaine d'essai. Tout se passe côté serveur : droit, échéance et
   * planification de la première semaine (edge function `start-trial`).
   * `trial_started` est émis là-bas, pas ici : un onglet fermé trop tôt ou un
   * refus de consentement ne doit pas faire disparaître l'étape du funnel.
   */
  const activateTrial = useCallback(async (): Promise<SubscriptionRow> => {
    const sub = await startTrial()
    setSubscription(sub)
    setAccess(await getAccessRights())
    return sub
  }, [])

  const startCheckout = useCallback(async (plan: SubscriptionPlan = SELF_SERVE_PLAN): Promise<void> => {
    const res = await supabase.functions.invoke("create-checkout-session", {
      body: { plan },
    })
    if (res.error || !res.data?.checkout_url) {
      throw new Error(res.error?.message ?? "Erreur création session de paiement")
    }
    window.location.href = res.data.checkout_url as string
  }, [])

  const openCustomerPortal = useCallback(async (): Promise<void> => {
    const res = await supabase.functions.invoke("create-portal-session", {
      body: {},
    })
    if (res.error || !res.data?.portal_url) {
      throw new Error(res.error?.message ?? "Erreur accès portail client")
    }
    window.location.href = res.data.portal_url as string
  }, [])

  // Droit calculé en base (vue access_rights), pas dans le navigateur.
  const isActive = access?.has_access ?? false
  const inTrial = access?.in_trial ?? false
  const trialEndsAt = access?.trial_ends_at ?? null
  const status: SubscriptionStatus | null = subscription?.status ?? null

  // Essai arrivé à son terme sans conversion : l'écran de fin d'essai (bilan +
  // les 4 offres) remplace le mur d'abonnement générique. Se lit sur la source
  // du droit, pas sur le statut seul : un abonnement Stripe annulé est aussi en
  // `trialing` nulle part, mais un essai converti puis résilié ne doit pas
  // retomber sur le bilan de sa semaine d'essai.
  const trialEnded =
    access?.source === "trial" && access.status === "trialing" && !access.has_access

  return {
    subscription,
    access,
    loading,
    status,
    isActive,
    inTrial,
    trialEnded,
    trialEndsAt,
    trialDaysLeft: computeTrialDaysLeft(trialEndsAt),
    activateTrial,
    startCheckout,
    openCustomerPortal,
    refresh: fetchSubscription,
  }
}
